import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setUnsavedChanges } from "../../../Redux/Slice/Common/SelectedTabSlice";
import {
  getPhoneNumberLimits,
  handlePhoneNoChange,
  phoneNumberValidation,
} from "../../../Common/Validations";
import MultiSelectDropdown from "../../../Common/MultiSelectDropdown";
import { ALL_CALLING_CODES, IdleMessagesData } from "../../../Common/Constants";
import TooltipSlider from "../../../Common/TooltipSlider";
import { getFunctionsList } from "../../../Redux/Slice/Functions/GetFunctionsListSlice";
import { maskNoByCallingCode } from "../../../Common/utils";
import CountryPhoneInput from "../../../Common/CountryPhoneInput";
import { useLocation } from "react-router-dom";

const AdvancedTab = ({
  isTabSelected,
  formData,
  setFormData,
  setHasChanged,
  formError,
  setFormError,
  userPlanFeatures,
  isMetaAssistant,
}) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const customerId = location.pathname.includes("/add-assistant/")
    ? location.pathname.split("/")[2]
    : "";
  const { functionListData } = useSelector((state) => state?.functionsList);

  const formattedFunctionsList = useMemo(() => {
    let _formattedDataList = Array.isArray(functionListData?.data)
      ? functionListData?.data?.map((functionItem) => {
          return {
            value: functionItem?.vapiFunctionId,
            label: functionItem?.function?.name,
          };
        })
      : [];
    return _formattedDataList;
  }, [functionListData, functionListData?.data]);

  const handleChangeFunctionsIds = (selectedList) => {
    const selectedListIds = Array.isArray(selectedList)
      ? selectedList.map((item) => item?.value)
      : [];
    setFormData((prev) => ({
      ...prev,
      advanced: {
        ...prev?.advanced,
        functions: {
          ...prev?.advanced?.functions,
          toolIds: selectedListIds,
        },
      },
    }));
  };

  const handleChange = (e, keyName) => {
    let { name, value, type, checked } = e.target;
    if (name == "phoneNumber") {
      handlePhoneNoChange(
        e,
        formData?.functions?.countryCallingCode,
        () =>
          setFormData((prev) => ({
            ...prev,
            advanced: {
              ...prev?.advanced,
              [keyName]: {
                ...prev?.advanced[keyName],
                [name]: value,
              },
            },
          })),
        (newError) =>
          setFormError({ ...formError, advancedPhoneError: newError }),
        (isChanged) => {
          setHasChanged(isChanged);
          dispatch(setUnsavedChanges(true));
        },
        false
      );
    } else if (name == "countryCallingCode") {
      setHasChanged(true);
      dispatch(setUnsavedChanges(true));
      let formatted = formData?.functions?.phoneNumber;
      setFormData((prev) => ({
        ...prev,
        advanced: {
          ...prev?.advanced,
          [keyName]: {
            ...prev?.advanced[keyName],
            [name]: value,
            phoneNumber: formatted,
          },
        },
      }));
      const minDigits = getPhoneNumberLimits(value)?.min ?? 10;
      const rawDigits = formData?.functions?.phoneNumber?.replace(/\D/g, "");
      const isValid = phoneNumberValidation(rawDigits, value);
      setFormError({
        ...formError,
        advancedPhoneError:
          isValid || !value || !formData?.functions?.phoneNumber
            ? ""
            : rawDigits.length < minDigits
            ? `Phone number must be at least ${minDigits} digits long.`
            : "Please enter a valid phone number",
      });
    } else if (name == "hipaaCompliance") {
      setHasChanged(true);
      dispatch(setUnsavedChanges(true));
      setFormData((prev) => ({
        ...prev,
        advanced: {
          ...prev?.advanced,
          [keyName]: {
            ...prev?.advanced[keyName],
            [name]: type == "checkbox" ? checked : value,
            audioRecording: false,
            videoRecording: false,
          },
        },
      }));
    } else if (name == "audioRecording") {
      setHasChanged(true);
      dispatch(setUnsavedChanges(true));
      setFormData((prev) => ({
        ...prev,
        setup: {
          ...prev?.setup,
          sendRecording: !checked ? false : prev?.setup?.sendRecording,
        },
        advanced: {
          ...prev?.advanced,
          [keyName]: {
            ...prev?.advanced[keyName],
            [name]: type == "checkbox" ? checked : value,
          },
        },
      }));
    } else {
      setHasChanged(true);
      dispatch(setUnsavedChanges(true));
      setFormData((prev) => ({
        ...prev,
        advanced: {
          ...prev?.advanced,
          [keyName]: {
            ...prev?.advanced[keyName],
            [name]: type == "checkbox" ? checked : value,
          },
        },
      }));
    }
  };

  useEffect(() => {
    dispatch(getFunctionsList({ keyword: "", customerId }));
  }, []);

  return (
    <div
      className={
        isTabSelected
          ? "tabsContent__content tabsContent__content--selected"
          : "tabsContent__content"
      }
    >
      <div className="assistantAdvancedList">
        <div className="assistantAdvancedList__row">
          <h5 className="title title--h5">Call Settings</h5>
          <div className="formRow">
            <div className="formCol">
              <div className="form__switchBtn form__switchBtn--directionColumn">
                <div className="form__switchBtn__option">
                  <label className="form__label">Backchanneling Enabled</label>
                  <div className="switchBtn">
                    <input
                      type="checkbox"
                      className="toggle"
                      id="backchanneling"
                      checked={formData?.callSettings?.backchanneling}
                      name="backchanneling"
                      onChange={(e) => handleChange(e, "callSettings")}
                    />
                    <label for="backchanneling">
                      <span className="on">ON</span>
                      <span className="off">OFF</span>
                    </label>
                  </div>
                </div>
                <div className="form__switchBtn__option">
                  <label className="form__label">
                    Background Denoising Enabled
                  </label>
                  <div className="switchBtn">
                    <input
                      type="checkbox"
                      className="toggle"
                      id="backgroundDenoising"
                      checked={formData?.callSettings?.backgroundDenoising}
                      name="backgroundDenoising"
                      onChange={(e) => handleChange(e, "callSettings")}
                    />
                    <label for="backgroundDenoising">
                      <span className="on">ON</span>
                      <span className="off">OFF</span>
                    </label>
                  </div>
                </div>
                <div className="form__switchBtn__option">
                  <label className="form__label">
                    Filter Injection Enabled
                  </label>
                  <div className="switchBtn">
                    <input
                      type="checkbox"
                      className="toggle"
                      id="filterInjection"
                      checked={formData?.callSettings?.filterInjection}
                      name="filterInjection"
                      onChange={(e) => handleChange(e, "callSettings")}
                    />
                    <label for="filterInjection">
                      <span className="on">ON</span>
                      <span className="off">OFF</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {userPlanFeatures?.HIPAA_COMPLIANCE ||
          userPlanFeatures?.AUDIO_RECORDING ||
          userPlanFeatures?.VIDEO_RECORDING ||
          isMetaAssistant ? (
            <div className="formRow">
              <div className="formCol">
                <div className="form__switchBtn form__switchBtn--directionColumn">
                  {userPlanFeatures?.HIPAA_COMPLIANCE || isMetaAssistant ? (
                    <div className="form__switchBtn__option">
                      <label className="form__label">HIPAA Compliance</label>
                      <div className="switchBtn">
                        <input
                          type="checkbox"
                          className="toggle"
                          id="hipaaCompliance"
                          name="hipaaCompliance"
                          checked={formData?.callSettings?.hipaaCompliance}
                          onChange={(e) => handleChange(e, "callSettings")}
                        />
                        <label for="hipaaCompliance">
                          <span className="on">ON</span>
                          <span className="off">OFF</span>
                        </label>
                      </div>
                    </div>
                  ) : null}
                  {userPlanFeatures?.AUDIO_RECORDING || isMetaAssistant ? (
                    <div className="form__switchBtn__option">
                      <label className="form__label">Audio Recording</label>
                      <div className="switchBtn">
                        <input
                          type="checkbox"
                          className="toggle"
                          id="audioRecording"
                          name="audioRecording"
                          disabled={formData?.callSettings?.hipaaCompliance}
                          checked={formData?.callSettings?.audioRecording}
                          onChange={(e) => handleChange(e, "callSettings")}
                        />
                        <label for="audioRecording">
                          <span className="on">ON</span>
                          <span className="off">OFF</span>
                        </label>
                      </div>
                    </div>
                  ) : null}
                  {userPlanFeatures?.VIDEO_RECORDING || isMetaAssistant ? (
                    <div className="form__switchBtn__option">
                      <label className="form__label">Video Recording</label>
                      <div className="switchBtn">
                        <input
                          type="checkbox"
                          className="toggle"
                          id="videoRecording"
                          name="videoRecording"
                          disabled={formData?.callSettings?.hipaaCompliance}
                          checked={formData?.callSettings?.videoRecording}
                          onChange={(e) => handleChange(e, "callSettings")}
                        />
                        <label for="videoRecording">
                          <span className="on">ON</span>
                          <span className="off">OFF</span>
                        </label>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
          <div className="formRow formRow__2">
            <div className="formCol">
              <div className="form__Field">
                <label for="idleMessage" className="form__label">
                  Idle Message
                </label>
                <MultiSelectDropdown
                  id="idleMessage"
                  name="idleMessage"
                  options={IdleMessagesData}
                  selectedValue={formData?.callSettings?.idleMessage}
                  onChange={(valData) => {
                    const selectedList = Array.isArray(valData)
                      ? valData.map((item) => item?.value)
                      : [];
                    setFormData((prev) => ({
                      ...prev,
                      advanced: {
                        ...prev?.advanced,
                        callSettings: {
                          ...prev?.advanced?.callSettings,
                          idleMessage: selectedList,
                        },
                      },
                    }));
                  }}
                />
              </div>
            </div>
            <div className="formCol">
              <div className="form__Field form__Field--range">
                <label for="styleExaggeration" className="form__label">
                  Max Idle Messages
                </label>
                <div className="form__rangeSlider">
                  <div className="form__rangeSlider__info">
                    <div className="form__rangeSlider__flex">
                      <p className="para">01</p>
                      <TooltipSlider
                        min="1"
                        max="10"
                        step={1}
                        name="maxIdleMessage"
                        value={formData?.callSettings?.maxIdleMessage}
                        onChange={(e) => handleChange(e, "callSettings")}
                      />
                      <p className="para">10</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="formRow formRow__2">
            <div className="formCol">
              <div className="form__Field form__Field--range">
                <label for="idleTimeout" className="form__label">
                  Idle Timeout
                </label>
                <div className="form__rangeSlider">
                  <div className="form__rangeSlider__info">
                    <div className="form__rangeSlider__flex">
                      <p className="para">5 (Sec)</p>
                      <TooltipSlider
                        min="5"
                        max="10"
                        step={0.1}
                        name="idleTimeout"
                        value={formData?.callSettings?.idleTimeout}
                        onChange={(e) => handleChange(e, "callSettings")}
                      />
                      <p className="para">10 (Sec)</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {userPlanFeatures?.VOICEMAIL_MESSAGE || isMetaAssistant ? (
            <div className="formRow">
              <div className="formCol">
                <div className="form__Field">
                  <label for="voiceMailMessage" className="form__label">
                    Voice Mail Message
                  </label>
                  <input
                    type="text"
                    id="voiceMailMessage"
                    className="form__input form__input--sm"
                    placeholder="Voice Mail Message"
                    name="voiceMailMessage"
                    value={formData?.callSettings?.voiceMailMessage}
                    onChange={(e) => handleChange(e, "callSettings")}
                  />
                </div>
              </div>
            </div>
          ) : null}

          {userPlanFeatures?.END_CALL_MESSAGE || isMetaAssistant ? (
            <div className="formRow">
              <div className="formCol">
                <div className="form__Field">
                  <label for="endMailMessage" className="form__label">
                    End Call Message
                  </label>
                  <input
                    type="text"
                    id="endMailMessage"
                    className="form__input form__input--sm"
                    placeholder="End Mail Message"
                    name="endMailMessage"
                    value={formData?.callSettings?.endMailMessage}
                    onChange={(e) => handleChange(e, "callSettings")}
                  />
                </div>
              </div>
            </div>
          ) : null}
        </div>
        <div className="assistantAdvancedList__row">
          <h5 className="title title--h5">Start Speaking Settings</h5>
          <div className="formRow formRow__2">
            <div className="formCol">
              <div className="form__Field form__Field--range">
                <label for="waitSeconds" className="form__label">
                  Wait Seconds
                </label>
                <div className="form__rangeSlider">
                  <div className="form__rangeSlider__info">
                    <div className="form__rangeSlider__flex">
                      <p className="para">0 (Sec)</p>
                      <TooltipSlider
                        min="0"
                        max="5"
                        step={0.1}
                        name="waitSeconds"
                        value={formData?.startSpeakingSettings?.waitSeconds}
                        onChange={(e) =>
                          handleChange(e, "startSpeakingSettings")
                        }
                      />
                      <p className="para">5 (Sec)</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="formCol">
              <div className="form__Field form__Field--range">
                <label for="onPunctuationSeconds" className="form__label">
                  On Punctuation Seconds
                </label>
                <div className="form__rangeSlider">
                  <div className="form__rangeSlider__info">
                    <div className="form__rangeSlider__flex">
                      <p className="para">0 (Sec)</p>
                      <TooltipSlider
                        min="0"
                        max="3"
                        step={0.1}
                        name="onPunctuationSeconds"
                        value={
                          formData?.startSpeakingSettings?.onPunctuationSeconds
                        }
                        onChange={(e) =>
                          handleChange(e, "startSpeakingSettings")
                        }
                      />
                      <p className="para">3 (Sec)</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="formRow formRow__2">
            <div className="formCol">
              <div className="form__Field form__Field--range">
                <label for="onNumberSeconds" className="form__label">
                  On Number Seconds
                </label>
                <div className="form__rangeSlider">
                  <div className="form__rangeSlider__info">
                    <div className="form__rangeSlider__flex">
                      <p className="para">0 (Sec)</p>
                      <TooltipSlider
                        min="0"
                        max="3"
                        step={0.1}
                        name="onNumberSeconds"
                        value={formData?.startSpeakingSettings?.onNumberSeconds}
                        onChange={(e) =>
                          handleChange(e, "startSpeakingSettings")
                        }
                      />
                      <p className="para">3 (Sec)</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="formCol">
              <div className="form__Field form__Field--range">
                <label for="onNoPunctuationSeconds" className="form__label">
                  On No Punctuation Seconds
                </label>
                <div className="form__rangeSlider">
                  <div className="form__rangeSlider__info">
                    <div className="form__rangeSlider__flex">
                      <p className="para">0 (Sec)</p>
                      <TooltipSlider
                        min="0"
                        max="3"
                        step={0.1}
                        name="onNoPunctuationSeconds"
                        value={
                          formData?.startSpeakingSettings
                            ?.onNoPunctuationSeconds
                        }
                        onChange={(e) =>
                          handleChange(e, "startSpeakingSettings")
                        }
                      />
                      <p className="para">3 (Sec)</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="formRow">
            <div className="formCol">
              <div className="form__switchBtn form__switchBtn--directionColumn">
                <div className="form__switchBtn__option">
                  <label className="form__label">Smart Endpointing</label>
                  <div className="switchBtn">
                    <input
                      type="checkbox"
                      className="toggle"
                      id="smartEndpoints"
                      name="smartEndpoints"
                      checked={formData?.startSpeakingSettings?.smartEndpoints}
                      onChange={(e) => handleChange(e, "startSpeakingSettings")}
                    />
                    <label for="smartEndpoints">
                      <span className="on">ON</span>
                      <span className="off">OFF</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <h5 className="title title--h5">End Speaking Settings</h5>
          <div className="formRow formRow__2">
            <div className="formCol">
              <div className="form__Field form__Field--range">
                <label for="numberofWords" className="form__label">
                  Number of Words
                </label>
                <div className="form__rangeSlider">
                  <div className="form__rangeSlider__info">
                    <div className="form__rangeSlider__flex">
                      <p className="para">0 (Sec)</p>
                      <TooltipSlider
                        min="0"
                        max="10"
                        step={1}
                        name="noOfWords"
                        value={formData?.endSpeakingSettings?.noOfWords}
                        onChange={(e) => handleChange(e, "endSpeakingSettings")}
                      />
                      <p className="para">10 (Sec)</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="formCol">
              <div className="form__Field form__Field--range">
                <label for="voiceSeconds" className="form__label">
                  Voice Seconds
                </label>
                <div className="form__rangeSlider">
                  <div className="form__rangeSlider__info">
                    <div className="form__rangeSlider__flex">
                      <p className="para">0 (Sec)</p>
                      <TooltipSlider
                        min="0"
                        max="0.5"
                        step={0.1}
                        name="voiceSeconds"
                        value={formData?.endSpeakingSettings?.voiceSeconds}
                        onChange={(e) => handleChange(e, "endSpeakingSettings")}
                      />
                      <p className="para">0.5 (Sec)</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="formRow formRow__2">
            <div className="formCol">
              <div className="form__Field form__Field--range">
                <label for="backoffSeconds" className="form__label">
                  Back off Seconds
                </label>
                <div className="form__rangeSlider">
                  <div className="form__rangeSlider__info">
                    <div className="form__rangeSlider__flex">
                      <p className="para">0 (Sec)</p>
                      <TooltipSlider
                        min="0"
                        max="10"
                        step={1}
                        name="backOffSeconds"
                        value={formData?.endSpeakingSettings?.backOffSeconds}
                        onChange={(e) => handleChange(e, "endSpeakingSettings")}
                      />
                      <p className="para">10 (Sec)</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <h5 className="title title--h5">Call Timeout Settings</h5>
          <div className="formRow formRow__2">
            <div className="formCol">
              <div className="form__Field form__Field--range">
                <label for="silentTimeout" className="form__label">
                  Silent Timeout
                </label>
                <div className="form__rangeSlider">
                  <div className="form__rangeSlider__info">
                    <div className="form__rangeSlider__flex">
                      <p className="para">10 (Sec)</p>
                      <TooltipSlider
                        min="10"
                        max="3600"
                        step={10}
                        name="silentTimeout"
                        value={formData?.callTimeoutSettings?.silentTimeout}
                        onChange={(e) => handleChange(e, "callTimeoutSettings")}
                      />
                      <p className="para">3600 (Sec)</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="formCol">
              <div className="form__Field form__Field--range">
                <label for="maximumDuration" className="form__label">
                  Maximum Duration
                </label>
                <div className="form__rangeSlider">
                  <div className="form__rangeSlider__info">
                    <div className="form__rangeSlider__flex">
                      <p className="para">10 (Sec)</p>
                      <TooltipSlider
                        min="10"
                        max="4300"
                        step={10}
                        name="maximumDuration"
                        value={formData?.callTimeoutSettings?.maximumDuration}
                        onChange={(e) => handleChange(e, "callTimeoutSettings")}
                      />
                      <p className="para">43200 (Sec)</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {!isMetaAssistant &&
        (userPlanFeatures?.CUSTOM_FUNCTIONS ||
          userPlanFeatures?.CALL_FORWARDING) ? (
          <div className="assistantAdvancedList__row">
            <h5 className="title title--h5">
              {userPlanFeatures?.CUSTOM_FUNCTIONS
                ? "Functions"
                : "Forwarding Phone Number"}
            </h5>
            {userPlanFeatures?.CUSTOM_FUNCTIONS ? (
              <div className="formRow">
                <div className="formCol">
                  <div className="form__Field">
                    <label for="tools" className="form__label">
                      Tools
                    </label>
                    <MultiSelectDropdown
                      id="toolIds"
                      name="toolIds"
                      options={formattedFunctionsList}
                      selectedValue={formData?.functions?.toolIds}
                      onChange={handleChangeFunctionsIds}
                    />
                  </div>
                </div>
              </div>
            ) : null}
            <div className="formRow formRow__2">
              {userPlanFeatures?.CUSTOM_FUNCTIONS ? (
                <div className="formCol">
                  <div className="form__switchBtn form__switchBtn--directionColumn">
                    <div className="form__switchBtn__option">
                      <label className="form__label">
                        Enable End Call Functions
                      </label>
                      <div className="switchBtn">
                        <input
                          type="checkbox"
                          className="toggle"
                          id="enableEndCallFunctions"
                          name="enableEndCallFunctions"
                          checked={formData?.functions?.enableEndCallFunctions}
                          onChange={(e) => handleChange(e, "functions")}
                        />
                        <label for="enableEndCallFunctions">
                          <span className="on">ON</span>
                          <span className="off">OFF</span>
                        </label>
                      </div>
                    </div>
                    <div className="form__switchBtn__option">
                      <label className="form__label">Dial Keypad</label>
                      <div className="switchBtn">
                        <input
                          type="checkbox"
                          className="toggle"
                          id="dialKeypad"
                          name="dialKeypad"
                          checked={formData?.functions?.dialKeypad}
                          onChange={(e) => handleChange(e, "functions")}
                        />
                        <label for="dialKeypad">
                          <span className="on">ON</span>
                          <span className="off">OFF</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
              {userPlanFeatures?.CALL_FORWARDING ? (
                <div className="formCol">
                  <div className="form__Field">
                    <label for="phonenumber" className="form__label">
                      Forwarding Phone Number
                    </label>
                    <CountryPhoneInput
                      countryCodeFieldName="countryCallingCode"
                      phoneFieldName="phoneNumber"
                      countryCode={formData?.functions?.countryCallingCode}
                      phoneNumber={formData?.functions?.phoneNumber}
                      onCountryChange={(e) => handleChange(e, "functions")}
                      onPhoneChange={(e) => handleChange(e, "functions")}
                    />
                    {formError?.advancedPhoneError && (
                      <div className="form__error">
                        {formError?.advancedPhoneError}
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      {/* Old Code */}

      {/* <div className="formRow formRow__2">
        <div className="formCol">
          <div className="form__Field">
            <label for="firstMessage" className="form__label">
              First Message <span style={{ color: "red" }}> * </span>
            </label>
            <input
              type="text"
              maxLength={120}
              id="firstMessage"
              name="firstMessage"
              onChange={handleChange}
              value={formData.firstMessage}
              className="form__input form__input--sm"
              placeholder="First Message"
            />
          </div>
        </div>
        <div className="formCol">
          <div className="form__Field">
            <label for="model" className="form__label">
              Model
            </label>
            <select
              className="form__select form__select--sm"
              id="model"
              name="model"
              onChange={handleChange}
              value={formData.model}
            >
              <option value="" disabled>
                -Select-
              </option>
              {transcriberModel
                ? transcriberModel.map((model) => (
                    <option value={model}>{model}</option>
                  ))
                : ""}
            </select>
          </div>
        </div>
      </div>
      <div className="formRow formRow__2">
        <div className="formCol">
          <div className="form__Field">
            <label for="language" className="form__label">
              Language
            </label>
            <select
              className="form__select form__select--sm"
              id="language"
              name="language"
              onChange={handleChange}
              value={formData.language}
            >
              {TranscriberLanguage
                ? TranscriberLanguage.map((lang) => (
                    <option value={lang}>{lang}</option>
                  ))
                : ""}
            </select>
          </div>
        </div>
      </div> */}

      {/* {} */}
      {/* <div className="formRow formRow__2">
        <div className="formCol">
          <div className="form__Field">
            <label for="backgroundSound" className="form__label">
              Type
            </label>
            <select
              className="form__select form__select--sm"
              id="backgroundSound"
              name="type"
              onChange={handleChange}
              value={formData.type}
            >
              <option value="" selected>
                -Select-
              </option>
              {functionType
                ? functionType.map((type1) => (
                    <option value={type1}>{type1}</option>
                  ))
                : ""}
            </select>
          </div>
        </div>
      </div>
      <div className="formRow">
        <div className="formCol">
          <div className="form__Field">
            <label for="functionName" className="form__label">
              Function Name{" "}
              {formData.type === "" ? (
                ""
              ) : (
                <span style={{ color: "red" }}> * </span>
              )}
            </label>
            <input
              type="text"
              id="functionName"
              name="functionName"
              maxLength={120}
              onChange={handleChange}
              value={formData.functionName}
              className="form__input form__input--sm"
              placeholder="Function Name"
            />
          </div>
        </div>
      </div>
      <div className="formRow">
        <div className="formCol">
          <div className="form__Field">
            <label for="functionPrompt" className="form__label">
              Function Prompt
            </label>
            <textarea
              id="functionPrompt"
              name="functionPrompt"
              onChange={handleChange}
              value={formData.functionPrompt}
              className="form__textarea"
              placeholder="Function Prompt"
            ></textarea>
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default AdvancedTab;
